import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Skeleton, baht, fmtCoins } from "../ui";
import api from "../../services/api";
import { errorInfo } from "../../services/errors";
import { paymentService } from "../../services/payment";
import { QR_EXPIRE_SECONDS, QR_POLL_MS, QR_INQUIRY_MS } from "../../services/config";

const SUCCESS = ["success", "settled", "completed"];
const FAILED = ["failed", "expired", "cancelled", "reject", "rejected"];
const MAX_AUTO_CONFIRM = 6; // payment_pending: ลองยืนยันซ้ำอัตโนมัติ ทุก 5 วิ ก่อนให้กดเอง
const clock = (s) => `${String(Math.floor(Math.max(0, s) / 60)).padStart(2, "0")}:${String(Math.max(0, s) % 60).padStart(2, "0")}`;

/**
 * ชำระด้วย QR (ปรับจาก PaymentQrModal ของ LevelUpCRM)
 * 1) createQrOrder -> 2) topup.init ทันที -> 3) poll getTransaction จน success -> 4) topup.confirm (payment_pending = รอ/ลองใหม่) -> 5) onConfirm อัปเดตยอด
 * onConfirm(qr) -> ผล confirm (throw = ยังเติมไม่สำเร็จ)
 */
export default function PaymentQrModal({ open, pkg, onClose, onConfirm }) {
  const [attempt, setAttempt] = useState(0); // เพิ่มค่า = สร้าง QR ใหม่
  const [qr, setQr] = useState(null);
  const [status, setStatus] = useState("creating");
  const [error, setError] = useState("");
  const [expiresAt, setExpiresAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [cf, setCf] = useState({ state: "idle", message: "", added: 0 });
  const confirming = useRef(false);
  const autoTries = useRef(0);
  const timer = useRef(null);
  const live = useRef(false);

  useEffect(() => {
    live.current = open;
    if (!open || !pkg) return undefined;
    let alive = true;
    confirming.current = false;
    autoTries.current = 0;
    clearTimeout(timer.current);
    setQr(null); setError(""); setStatus("creating"); setCf({ state: "idle", message: "", added: 0 });
    const isCredit = pkg.kind === "credit";
    paymentService.createQrOrder({ amount: pkg.price, remark: isCredit ? `ซื้อสิทธิ์สร้างตัวละคร ${pkg.qty} ตัว` : `เติมคอยน์ ${pkg.name}`, packageId: pkg.id })
      // ผูก transaction กับผู้ใช้+แพ็กที่ฐานข้อมูลทันที (ฝั่ง server คำนวณราคาเอง) ไม่งั้น confirm จะเติมให้ไม่ได้
      .then(async (r) => { await (isCredit ? api.credits.init(r.transactionId, pkg.qty) : api.coins.topupInit(r.transactionId, pkg.id, pkg.id === "custom" ? pkg.price : undefined)); return r; })
      .then((r) => {
        if (!alive) return;
        setQr(r);
        setStatus("pending");
        setExpiresAt(r.expireDatetime ? new Date(r.expireDatetime).getTime() : Date.now() + QR_EXPIRE_SECONDS * 1000);
      })
      .catch((e) => { if (alive) { setError(errorInfo(e).message); setStatus("error"); } });
    return () => { alive = false; live.current = false; clearTimeout(timer.current); };
  }, [open, pkg, attempt]);

  const poll = useCallback(async () => {
    if (!qr?.transactionId) return;
    const d = await paymentService.getTransaction(qr.transactionId);
    if (d?.status) setStatus(String(d.status).toLowerCase());
  }, [qr]);

  useEffect(() => {
    if (!open || !qr?.transactionId || SUCCESS.includes(status) || FAILED.includes(status)) return undefined;
    const a = setInterval(() => poll().catch(() => {}), qr.mock ? 800 : QR_POLL_MS);
    const b = setInterval(() => paymentService.inquiry(qr.transactionId).catch(() => {}), QR_INQUIRY_MS);
    return () => { clearInterval(a); clearInterval(b); };
  }, [open, qr, status, poll]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  const confirmNow = useCallback(async () => {
    if (confirming.current || !qr) return;
    confirming.current = true;
    clearTimeout(timer.current);
    setCf((c) => ({ ...c, state: "confirming", message: "" }));
    try {
      const r = await onConfirm(qr);
      setCf({ state: "done", message: "", added: Number(r?.added ?? r?.coins) || 0 });
    } catch (e) {
      const info = errorInfo(e);
      if (info.code === "payment_pending" && autoTries.current < MAX_AUTO_CONFIRM) {
        autoTries.current += 1;
        setCf({ state: "waiting", message: info.message, added: 0 });
        timer.current = setTimeout(() => { if (live.current) confirmNow(); }, 5000);
      } else {
        setCf({ state: info.code === "payment_pending" ? "waiting-manual" : "error", message: info.message, added: 0 });
      }
    } finally {
      confirming.current = false;
    }
  }, [qr, onConfirm]);

  useEffect(() => {
    if (SUCCESS.includes(status) && cf.state === "idle") confirmNow();
  }, [status, cf.state, confirmNow]);

  const retryConfirm = () => { autoTries.current = 0; confirmNow(); };
  const paid = SUCCESS.includes(status);
  const secondsLeft = Math.round((expiresAt - now) / 1000);
  const expired = !paid && !!qr && (FAILED.includes(status) || secondsLeft <= 0);
  const done = cf.state === "done";

  return (
    <Modal open={open} title="ชำระเงินด้วย QR" size="sm" onClose={onClose} footer={<Button variant={done ? "primary" : undefined} onClick={onClose}>{done ? "เสร็จสิ้น" : "ปิด"}</Button>}>
      <div className="center">
        <div className="strong">{pkg?.name}</div>
        <div style={{ fontSize: 28, fontWeight: 800, margin: "4px 0" }}>{baht(pkg?.price)}</div>
        <div className="muted" style={{ fontSize: 13 }}>{pkg?.kind === "credit" ? `ได้รับ ${fmtCoins(pkg.qty)} สิทธิ์สร้างตัวละคร` : `ได้รับ ${fmtCoins(pkg?.tokens)} คอยน์${pkg?.clubPoint ? ` · ${fmtCoins(pkg.clubPoint)} คุยพ้อยท์` : ""}`}</div>

        {status === "creating" ? <Skeleton height={240} style={{ margin: "12px auto", maxWidth: 260 }} /> : null}
        {error ? (
          <div className="notice notice--error mt-16" role="alert" style={{ flexDirection: "column", alignItems: "stretch" }}>
            <div className="strong">สร้าง QR ไม่สำเร็จ</div><div style={{ fontSize: 13 }}>{error}</div>
            <Button size="sm" onClick={() => setAttempt((n) => n + 1)}>ลองสร้างใหม่</Button>
          </div>
        ) : null}
        {qr?.base64 ? <div className="qr-box" style={{ opacity: expired ? 0.25 : 1 }}><img src={qr.base64} alt="QR สำหรับชำระเงิน" /></div> : null}
        {qr?.base64 && !paid && !expired ? <a className="btn btn--sm" href={qr.base64} download={`aitalk-qr-${qr.refNo || "pay"}.png`}>บันทึกรูป QR</a> : null}

        {done ? (
          <div className="notice notice--ok mt-16" role="status" style={{ justifyContent: "center" }}><b>{pkg?.kind === "credit" ? `ชำระเงินสำเร็จ — เพิ่ม ${fmtCoins(cf.added || pkg?.qty)} สิทธิ์สร้างตัวละครแล้ว` : `ชำระเงินสำเร็จ — เติม ${fmtCoins(cf.added || pkg?.tokens)} คอยน์เข้าบัญชีแล้ว`}</b></div>
        ) : paid ? (
          <div className="card mt-16" role="status">
            {cf.state === "error" ? (
              <>
                <div className="strong">ชำระเงินสำเร็จ แต่ยังเติมคอยน์ไม่เสร็จ</div>
                <div className="error-text mt-8">{cf.message}</div>
                <p className="muted mt-8" style={{ fontSize: 13 }}>ระบบจะไม่เรียกเก็บซ้ำ กดลองใหม่หรือแจ้งทีมงานพร้อมเลข Ref {qr?.refNo}</p>
                <Button className="mt-8" onClick={retryConfirm}>ลองเติมคอยน์อีกครั้ง</Button>
              </>
            ) : cf.state === "waiting-manual" ? (
              <>
                <div className="strong">รับยอดชำระแล้ว กำลังรอระบบยืนยัน</div>
                <p className="muted mt-8" style={{ fontSize: 13 }}>{cf.message || "ยังไม่พบการชำระเงินฝั่งระบบ"} — คอยน์เข้าภายใน 5-10 นาที ปิดหน้านี้ได้ แล้วกดตรวจสอบที่หน้าเติมคอยน์ภายหลัง</p>
                <Button className="mt-8" variant="primary" onClick={retryConfirm}>ตรวจสอบอีกครั้ง</Button>
              </>
            ) : (
              <><div className="strong">{cf.state === "waiting" ? "ชำระเงินแล้ว กำลังรอระบบยืนยัน..." : "ชำระเงินแล้ว กำลังเติมคอยน์..."}</div>
                <div className="faint mt-8" style={{ fontSize: 12 }}>{cf.state === "waiting" ? "ตรวจสอบซ้ำอัตโนมัติ ไม่ต้องจ่ายอีก" : "รอสักครู่"}</div></>
            )}
          </div>
        ) : expired ? (
          <div className="notice notice--error mt-16" role="alert" style={{ flexDirection: "column", alignItems: "stretch" }}>
            <div>QR หมดอายุหรือรายการถูกยกเลิก</div>
            <Button size="sm" variant="primary" onClick={() => setAttempt((n) => n + 1)}>สร้าง QR ใหม่</Button>
          </div>
        ) : qr ? (
          <div className="card mt-16"><div className="faint" style={{ fontSize: 12 }}>QR หมดอายุใน</div><div style={{ fontSize: 24, fontWeight: 800 }} aria-live="off">{clock(secondsLeft)}</div>
            <div className="faint" style={{ fontSize: 12 }}>ระบบตรวจสอบการชำระเงินอัตโนมัติ (คอยน์เข้าภายใน 5-10 นาที)</div></div>
        ) : null}
        {qr?.mock && !paid ? <Button className="mt-16" onClick={() => paymentService.mockMarkPaid(qr.transactionId)}>จำลองจ่ายสำเร็จ (โหมดทดลอง)</Button> : null}
        {qr?.refNo ? <div className="faint mt-8" style={{ fontSize: 12 }}>Ref: {qr.refNo}</div> : null}
      </div>
    </Modal>
  );
}
